import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import { agentApiClient } from '../../agent_api_client';
import { NEW_COLLECTION_MODAL } from '../../constants/modals';
import { openModal } from '../../ducks/modals_manager/slice';
import { getIsAiMetadataEnrichmentFeatureEnabled } from '../../ducks/settings/select_features';
import { ICONS } from '../../icons/icon';
import type { AppDispatch, AppState } from '../../store/types';
import { DYNAMIC_ICON } from '../../ui/menus/menu/constants';
import { shouldShowCreateCollectionForm } from '../../utils/collections';
import { selectUserRoles } from '../../utils/get_user_roles';
import separateMenuGroups from '../../utils/separate_menu_groups';
import type { ContextMenuItemDefinition } from '../context_menu/types';
import { getCreateCollectionActionLabel } from '../context_menu/utils';
import { options } from './context_menu_options';
import {
  type ContextItemsAttributes,
  onCustomAction,
} from './context_menu_utils';

export default function getOptions(
  state: AppState,
  contextItems: SearchItemEntity[],
  attributes: ContextItemsAttributes,
): ContextMenuItemDefinition[] {
  const [contextItem] = contextItems;
  const { userRoles } = state.rolesReducer;

  const {
    canWrite,
    canShareEntity,
    canChangeEntityAcl,
    canDeleteCollection,
    selected,
  } = attributes;
  const topItems: ContextMenuItemDefinition[] = [];
  const items: ContextMenuItemDefinition[] = [];
  const isAllItemsFavorite = contextItems.every((item) => item.favoured);
  const isAllItemsNoneFavorite = contextItems.every((item) => !item.favoured);
  const areFavoritesEnabled = !state.settings.merged.hide_favourites;
  const isAiMetadataEnabled =
    state.settings.merged.enable_ai_metadata_filling &&
    getIsAiMetadataEnrichmentFeatureEnabled(state);

  const collectionId = contextItem.id;
  const roles = selectUserRoles(state);
  const showNewCollectionButton = shouldShowCreateCollectionForm(
    collectionId,
    roles.canWriteCollections,
    roles.canCreateCollections,
    roles.canCreateRootCollections,
  );

  const readOnly = collectionId ? !canWrite : false;
  if (selected === 1 && showNewCollectionButton && !readOnly) {
    topItems.push({
      label: getCreateCollectionActionLabel(collectionId),
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: NEW_COLLECTION_MODAL,
            contextItems: collectionId ? contextItems : undefined,
          }),
        ),
      icon: ICONS.Collection,
    });
  }
  const customActionsCollection = userRoles.canReadCustomActions
    ? (state.customActions.customActionsContexts.COLLECTION ?? [])
    : [];
  const customActionsBulk = userRoles.canReadCustomActions
    ? (state.customActions.customActionsContexts.BULK ?? [])
    : [];

  // Temporarily commented out until collection notifications work in the backend.
  // Revert PR #1126 to bring this feature back
  // if (
  //   selected === 1 &&
  //   userRoles.canReadNotifications &&
  //   userRoles.canReadSubscriptions
  // ) {
  //   const subscriptions = collectionsSubscriptions[contextItem.id];
  //   if (subscriptions && subscriptions.length) {
  //     topItems.push({
  //       label: 'Unsubscribe from updates',
  //       action: () => (dispatch: AppDispatch) =>
  //         dispatch(unsubscribeFromCollection(contextItem.id)),
  //       icon: ICONS.BellCrossed,
  //     });
  //   } else {
  //     topItems.push({
  //       label: 'Subscribe to updates',
  //       action: () => (dispatch: AppDispatch) =>
  //         dispatch(subscribeToCollection(contextItem.id)),
  //       icon: ICONS.Bell,
  //     });
  //   }
  // }

  if (userRoles.canWriteMetadataValues) {
    if (selected === 1) {
      const metadataSubItems: ContextMenuItemDefinition[] = [];

      if (userRoles.canWriteCollections && canWrite) {
        metadataSubItems.push(
          options.COLLECTION_METADATA_UPDATE(contextItems, true),
        );
      }
      if (userRoles.canWriteAssets) {
        metadataSubItems.push(
          options.COLLECTION_METADATA_UPDATE_BULK(contextItems),
        );
      }
      if (isAiMetadataEnabled && userRoles.canTriggerMetadataAiFilling) {
        metadataSubItems.push(
          options.AI_METADATA_BULK(
            contextItems,
            'collections',
            'collection menu',
          ),
        );
      }
      if (metadataSubItems.length > 1) {
        items.push(options.COLLECTION_METADATA(metadataSubItems));
      }
      if (metadataSubItems.length === 1) {
        items.push(metadataSubItems[0]);
      }
    } else if (userRoles.canWriteCollections) {
      items.push(options.ENTITY_METADATA_UPDATE(contextItems, true));
    }
  }

  const reviewSubitems = [
    options.ENTITY_APPROVE(contextItems, OBJECT_TYPE_COLLECTIONS, false),
    options.ENTITY_REJECT(contextItems, OBJECT_TYPE_COLLECTIONS, false),
    options.ENTITY_RESET(contextItems, OBJECT_TYPE_COLLECTIONS, false),
  ];

  if (
    contextItems.length === 1 &&
    userRoles.canApproveWithoutRequest &&
    userRoles.canWriteApprovalStatusInBulk
  ) {
    items.push(options.ENTITY_REVIEW(reviewSubitems, false));
  }

  if (
    userRoles.canWriteFavorites &&
    isAllItemsNoneFavorite &&
    areFavoritesEnabled
  ) {
    items.push(options.ADD_TO_FAVORITES(contextItems, true));
  }

  if (
    userRoles.canDeleteFavorites &&
    isAllItemsFavorite &&
    areFavoritesEnabled
  ) {
    items.push(options.REMOVE_FROM_FAVORITES(contextItems, true));
  }
  if (userRoles.canWriteShares) {
    if ((selected === 1 && canShareEntity) || selected > 1) {
      items.push(options.ENTITY_SHARE(contextItems));
    }
  }

  if (userRoles.canWriteAcls) {
    if ((selected === 1 && canChangeEntityAcl) || selected > 1) {
      items.push(options.ENTITY_ACCESS_CONTROL(contextItems, true));
    }
  }

  const downloadSubitems = [];

  if (userRoles.webCanDownloadProxy) {
    const disabled = contextItem.is_blocked ?? false;
    downloadSubitems.push(
      options.ENTITY_DOWNLOAD_PROXY(contextItems, true, disabled),
    );
  }

  if (
    userRoles.webCanDownloadOriginal &&
    userRoles.canReadFormats &&
    userRoles.canReadFiles
  ) {
    const disabled = contextItem.is_blocked ?? false;
    downloadSubitems.push(
      options.ENTITY_DOWNLOAD_ORIGINAL(contextItems, true, disabled),
    );

    if (agentApiClient.hasFeature('DOWNLOAD_ALL_FORMATS')) {
      downloadSubitems.push(
        options.ENTITY_DOWNLOAD_ALL_FORMATS(contextItems, true, disabled),
      );
    }

    if (agentApiClient.hasFeature('OPEN_IN_FCP')) {
      items.push(
        options.ENTITY_OPEN_ORIGINAL_IN_FCP(contextItems, true, disabled),
      );
    }
  }
  if (downloadSubitems.length > 1) {
    items.push(options.ENTITY_DOWNLOAD(downloadSubitems, true));
  }
  if (downloadSubitems.length === 1) {
    downloadSubitems[0].label = `Download ${downloadSubitems[0].label}`;
    items.push(downloadSubitems[0]);
  }

  if (userRoles.canReadExportLocations) {
    items.push(options.ENTITY_EXPORT(contextItems, true));
  }

  if (userRoles.canWriteTransfers) {
    items.push(options.ENTITY_TRANSFER(contextItems, true));
  }

  if (userRoles.canArchiveFormats) {
    items.push(options.ENTITY_ARCHIVE(contextItems, true));
  }

  if (userRoles.canReindexAssets) {
    items.push(options.COLLECTION_BULK_REINDEX(contextItems, true));
  }

  if (userRoles.canRestoreArchivedFormats) {
    items.push(options.ENTITY_RESTORE(contextItems, true));
  }

  if (userRoles.canCreateTranscodeJobs && canWrite) {
    items.push(options.ENTITY_RETRANSCODE(contextItems, true));
  }

  if (userRoles.canAnalyzeContent) {
    items.push(options.ENTITY_ANALYZE(contextItems, true));
  }
  const enableFaceRecognition = state.settings.merged.enable_face_recognition;
  if (enableFaceRecognition && userRoles.canTriggerFaceRecognition) {
    items.push(options.ENTITY_FACE_RECOGNITION_TRIGGER(contextItems, true));
  }

  if (userRoles.canTranscribeContent) {
    items.push(options.ENTITY_TRANSCRIBE(contextItems, true));
  }

  if (contextItem.external_id && contextItem.storage_id && canWrite) {
    items.push(options.COLLECTION_RE_MAP(contextItems));
  }

  const coverImageSubitems: ContextMenuItemDefinition[] = [];

  if (userRoles.canWriteKeyframes) {
    coverImageSubitems.push(
      options.COLLECTION_COVER_IMAGE_UPLOAD(contextItems),
    );
  }

  if (userRoles.canWriteTranscodeJobs) {
    coverImageSubitems.push(
      options.COLLECTION_COVER_IMAGE_REGENERATE(contextItems),
    );
  }

  if (
    canWrite &&
    userRoles.canWriteKeyframes &&
    userRoles.webEnablePosters &&
    contextItem.custom_keyframe
  ) {
    coverImageSubitems.push(
      options.COLLECTION_COVER_IMAGE_DELETE(contextItems),
    );
  }

  if (coverImageSubitems.length > 1) {
    items.push(options.COLLECTION_COVER_IMAGE(coverImageSubitems));
  }
  if (coverImageSubitems.length === 1) {
    const [subitem] = coverImageSubitems;
    items.push({
      ...subitem,
      label: `Cover Image ${subitem.label}`,
    });
  }

  items.push(options.ENTITY_COLLECTION_INFO(contextItems, false, false));

  const itemsCustomActionsGroup: ContextMenuItemDefinition[] = [];

  (selected === 1 ? customActionsCollection : customActionsBulk)
    .filter((customAction) => !customAction.disabled)
    .forEach((customAction) => {
      itemsCustomActionsGroup.push({
        icon: DYNAMIC_ICON,
        label: customAction.title,
        action: () => (dispatch: AppDispatch) =>
          onCustomAction(
            dispatch,
            contextItems,
            customAction,
            attributes.fromCollectionId,
          ),
        multiple: true,
      });
    });

  const itemsDeleteGroup: ContextMenuItemDefinition[] = [];

  if (userRoles.canDeleteFiles) {
    itemsDeleteGroup.push(
      options.DELETE_FILES_FROM_STORAGE(contextItems, true),
    );
  }
  const containsCollection = contextItems.some(
    (item) => item.object_type === OBJECT_TYPE_COLLECTIONS,
  );
  if (
    containsCollection &&
    (userRoles.canDeleteAssets || userRoles.canDeleteCollections)
  ) {
    if ((selected === 1 && canDeleteCollection) || selected > 1) {
      itemsDeleteGroup.push(options.COLLECTION_DELETE(contextItems, true));
    }
  }

  const list = separateMenuGroups([
    topItems,
    items,
    itemsCustomActionsGroup,
    itemsDeleteGroup,
  ]);

  return list;
}
